/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useEffect, useRef, useState } from "react";
import { BEM } from "@adapptio/lib-components";
import { isFunction } from "@adapptio/lib-shared";
import { CallbackRef, useForkRef } from "../../../hooks";
import { IDndDraggableInfo, TDndDropAreaID } from "../Dnd/DndTypes";
import { useDndContext } from "../Dnd/DndContext";
import { DndPlaceholder } from "../DndPlaceholder";
import * as attributes from "../Dnd/dndAttributes";
import { DndDropAreaContext, IDndDropAreaContext } from "./DndDropAreaContext";
import { useDeepCompareMemo } from "use-deep-compare";

export interface IDndDropAreaProps extends React.ComponentPropsWithRef<"div"> {
	className?: string;
	droppableId: TDndDropAreaID;
	children:
		| React.ReactNode
		| ((props: {
				droppableRef: CallbackRef;
				droppableProps: Record<string, string>;
				placeholder: React.ReactNode;
		  }) => JSX.Element);
}

export const DndDropArea = React.forwardRef<HTMLDivElement, IDndDropAreaProps>(
	({ children, className, droppableId, ...props }, ref) => {
		const baseClass = BEM("edt-dnd-droppable");

		const droppableRef = useRef<HTMLDivElement>();
		const forkRef = useForkRef(droppableRef, ref);

		const { registry, contextId } = useDndContext();

		const [ dragging, setDragging ] = useState<IDndDraggableInfo>(null);

		const droppableProps = {
			[attributes.droppable.droppableId]: droppableId,
			[attributes.droppable.contextId]: contextId
		};

		if (className) {
			baseClass.add(className);
		}

		const placeholder = useDeepCompareMemo(
			() => (dragging ? <DndPlaceholder contextId={contextId} draggableInfo={dragging} /> : null),
			[ dragging, contextId ]
		);

		const droppableContextValue = useDeepCompareMemo<IDndDropAreaContext>(
			() => ({
				droppableId,
				draggignOver: !!dragging
			}),
			[ droppableId, dragging ]
		);

		useEffect(() => {
			registry.droppable.register({
				id: droppableId,
				onDragMove: ({ dragging, draggingOver }) => {
					setDragging(() => (draggingOver === droppableId ? dragging : null));
				},
				onDragDrop: () => {
					setDragging(null);
				}
			});

			return () => registry.droppable.unregister(droppableId);
		}, [ droppableId ]);

		return (
			<DndDropAreaContext.Provider value={droppableContextValue}>
				{isFunction(children) ? (
					children({ droppableRef: forkRef, droppableProps, placeholder })
				) : (
					<div className={baseClass.toClassName()} ref={forkRef} {...props} {...droppableProps}>
						{children}
						{placeholder}
					</div>
				)}
			</DndDropAreaContext.Provider>
		);
	}
);
